/**
 * Timezone Conversion Utilities (Phase 4E)
 * 
 * Purpose: Convert UTC timestamps to EST/EDT with both relative and absolute time display
 * Team Timezone: America/New_York (handles DST automatically)
 * 
 * Format Examples:
 * - Relative: "in 2 hours", "2 hours ago", "in 3 days"
 * - Absolute: "3:00 PM EST", "Nov 15 at 3:00 PM EST"
 * - Combined: "in 2 hours | 3:00 PM EST"
 */

import { formatDistance, format, isToday, isTomorrow, isPast } from 'date-fns';

/**
 * Format relative time from now
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Relative time string (e.g., "in 2 hours", "2 hours ago")
 */
export const formatRelativeTime = (utcTimestamp) => {
  if (!utcTimestamp) return 'N/A';
  
  try {
    const targetDate = new Date(utcTimestamp);
    const now = new Date();
    
    // Check if time is in the past or future
    const isFuture = targetDate > now;
    
    // Use formatDistance for human-readable relative time
    const distance = formatDistance(targetDate, now, { addSuffix: true });
    
    return distance; // Returns: "in 2 hours", "2 hours ago", "in 3 days", etc.
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return 'Invalid date';
  }
};

/**
 * Format absolute time in EST/EDT timezone
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Absolute time string (e.g., "3:00 PM EST", "Nov 15 at 3:00 PM EST")
 */
export const formatAbsoluteTime = (utcTimestamp) => {
  if (!utcTimestamp) return 'N/A';
  
  try {
    const targetDate = new Date(utcTimestamp);
    
    // Create formatter for EST/EDT timezone (America/New_York)
    // This automatically handles DST transitions
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      month: 'short',
      day: 'numeric',
      timeZoneName: 'short' // Shows "EST" or "EDT" automatically
    });
    
    const formattedDate = formatter.format(targetDate);
    
    // Check if it's today or tomorrow for special formatting
    if (isToday(targetDate)) {
      // For today, show only time
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
      return `Today at ${timeFormatter.format(targetDate)}`;
    } else if (isTomorrow(targetDate)) {
      // For tomorrow, show "Tomorrow at TIME"
      const timeFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
      return `Tomorrow at ${timeFormatter.format(targetDate)}`;
    }
    
    // For other dates, show full date with time
    return formattedDate;
  } catch (error) {
    console.error('Error formatting absolute time:', error);
    return 'Invalid date';
  }
};

/**
 * Format combined time (relative + absolute)
 * 
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @returns {string} Combined time string (e.g., "in 2 hours | 3:00 PM EST")
 */
export const formatCombinedTime = (utcTimestamp) => {
  if (!utcTimestamp) return 'N/A';
  
  try {
    const relative = formatRelativeTime(utcTimestamp);
    const absolute = formatAbsoluteTime(utcTimestamp);
    
    // Combine both formats with separator
    return `${relative} | ${absolute}`;
  } catch (error) {
    console.error('Error formatting combined time:', error);
    return 'Invalid date';
  }
};

/**
 * Get meeting status based on scheduled time and duration
 * 
 * @param {string|Date} scheduledTime - Scheduled start time (UTC)
 * @param {number} durationMinutes - Meeting duration in minutes
 * @returns {string} Meeting status: 'scheduled' | 'in-progress' | 'ended'
 */
export const getMeetingStatus = (scheduledTime, durationMinutes = 45) => {
  if (!scheduledTime) return 'scheduled';
  
  try {
    const now = new Date();
    const start = new Date(scheduledTime);
    const end = new Date(start.getTime() + durationMinutes * 60000); // Add duration in milliseconds
    
    if (now < start) {
      return 'scheduled'; // Future meeting
    } else if (now >= start && now < end) {
      return 'in-progress'; // Currently happening
    } else {
      return 'ended'; // Past meeting
    }
  } catch (error) {
    console.error('Error getting meeting status:', error);
    return 'scheduled';
  }
};

/**
 * Get status badge configuration (color, icon, label)
 * 
 * @param {string} status - Meeting status from getMeetingStatus
 * @returns {object} Badge configuration with color, label, and icon
 */
export const getStatusBadgeConfig = (status) => {
  const configs = {
    'scheduled': {
      color: 'bg-blue-500',
      textColor: 'text-white',
      label: 'Scheduled',
      icon: '🔵'
    },
    'in-progress': {
      color: 'bg-green-500',
      textColor: 'text-white',
      label: 'In Progress',
      icon: '🟢'
    },
    'ended': {
      color: 'bg-gray-500',
      textColor: 'text-white',
      label: 'Ended',
      icon: '⚫'
    }
  };
  
  return configs[status] || configs['scheduled'];
};

/**
 * Format date for display (simplified)
 * 
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted date string
 */
export const formatDateSimple = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return format(date, 'MMM dd, yyyy');
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format datetime for display (with time)
 * 
 * @param {string|Date} timestamp - Timestamp to format
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeSimple = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  try {
    const date = new Date(timestamp);
    return format(date, 'MMM dd, yyyy h:mm a');
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return 'Invalid date';
  }
};
